'use client';

import {
  EditorContent,
  Extensions,
  JSONContent,
  useEditor,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Minus,
  Undo,
  Redo,
} from 'lucide-react';
import { Toggle } from '../../toggle';
import { cn } from '@/lib/utils';
import { Separator } from '../../separator';
import { Button } from '../../button';
import { Level } from '@tiptap/extension-heading';
import { CharacterCount } from '@tiptap/extensions';
import Document from '@tiptap/extension-document';
import { useEffect } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';

interface MinimalTiptapFunctionsConfig {
  bold?: boolean;
  italic?: boolean;
  strikethrough?: boolean;
  code?: boolean;
  headingLevels?: number[]; // e.g., [1, 2, 3]
  bulletList?: boolean;
  orderedList?: boolean;
  blockquote?: boolean;
  horizontalRule?: boolean;
  undoRedo?: boolean;
}

interface MinimalTiptapProps {
  content?: JSONContent;
  onChange?: (content: JSONContent) => void;
  contentAsText?: string;
  setContentAsText?: (content: string) => void;
  placeholder?: string;
  editable?: boolean;
  className?: string;
  /** Classes of the editable area (defaults to a 200px tall box). */
  editorClassName?: string;
  functionConfig?: MinimalTiptapFunctionsConfig;
  limit?: number;
  minLenght?: number;
  setError?: (error: string) => void;
  oneLine?: boolean;
  onFocus?: () => void;
}

const SingleLineDocument = Document.extend({
  content: 'text*',
});

function MinimalTiptap({
  content = {},
  setContentAsText,
  onChange,
  placeholder = 'Start typing...',
  editable = true,
  className,
  editorClassName = 'min-h-[200px] p-4 border-0',
  functionConfig = {
    bold: true,
    italic: true,
    strikethrough: true,
    code: true,
    headingLevels: [1, 2, 3],
    bulletList: true,
    orderedList: true,
    blockquote: true,
    horizontalRule: true,
    undoRedo: true,
  },
  limit,
  minLenght = 0,
  setError,
  oneLine = false,
  onFocus,
}: MinimalTiptapProps) {
  const { LL } = useI18nContext();

  let extensions: Extensions = [
    StarterKit.configure({
      italic: functionConfig.italic ? undefined : false,
      bold: functionConfig.bold ? undefined : false,
      strike: functionConfig.strikethrough ? undefined : false,
      code: functionConfig.code ? undefined : false,
      heading: functionConfig.headingLevels
        ? { levels: functionConfig.headingLevels as Level[] }
        : false,
      blockquote: functionConfig.blockquote ? undefined : false,
      horizontalRule: functionConfig.horizontalRule ? undefined : false,
      undoRedo: functionConfig.undoRedo ? undefined : false,
      bulletList: {
        keepMarks: true,
        keepAttributes: false,
      },
      orderedList: {
        keepMarks: true,
        keepAttributes: false,
      },
    }),
    CharacterCount.configure({
      limit,
    }),
  ];

  if (oneLine) {
    extensions = [...extensions, SingleLineDocument];
  }

  const editor = useEditor({
    extensions,
    content,
    editable,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getJSON());
      setContentAsText?.(editor.getText());
      if (editor.getText().length < minLenght) {
        setError?.(
          LL.appCustomizationPage.couponBanner.richTextMinLengthError({
            minLenght,
          })
        );
      } else {
        setError?.('');
      }
    },
    editorProps: {
      attributes: {
        class: cn(
          // 'prose prose-sm sm:prose-base lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
          editorClassName
        ),
      },
    },
  });

  useEffect(() => {
    editor.setEditable(editable);
  }, [editable, editor]);

  useEffect(() => {
    if (content !== editor.getJSON()) {
      editor.commands.setContent(content || '');
      setContentAsText?.(editor.getText());
    }
  }, [content, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div
      className={cn('relative border rounded-lg overflow-hidden', className)}
    >
      <div className="border-b p-2 flex flex-wrap items-center gap-1">
        {functionConfig.bold && (
          <Toggle
            size="sm"
            pressed={editor.isActive('bold')}
            onPressedChange={() => editor.chain().focus().toggleBold().run()}
            disabled={
              !editor.can().chain().focus().toggleBold().run() || !editable
            } // keep active if already bold
          >
            <Bold className="h-4 w-4" />
          </Toggle>
        )}

        {functionConfig.italic && (
          <Toggle
            size="sm"
            pressed={editor.isActive('italic')}
            onPressedChange={() => editor.chain().focus().toggleItalic().run()}
            disabled={
              !editor.can().chain().focus().toggleItalic().run() || !editable
            }
          >
            <Italic className="h-4 w-4" />
          </Toggle>
        )}

        {functionConfig.strikethrough && (
          <Toggle
            size="sm"
            pressed={editor.isActive('strike')}
            onPressedChange={() => editor.chain().focus().toggleStrike().run()}
            disabled={
              !editor.can().chain().focus().toggleStrike().run() || !editable
            }
          >
            <Strikethrough className="h-4 w-4" />
          </Toggle>
        )}

        {functionConfig.code && (
          <Toggle
            size="sm"
            pressed={editor.isActive('code')}
            onPressedChange={() => editor.chain().focus().toggleCode().run()}
            disabled={
              !editor.can().chain().focus().toggleCode().run() || !editable
            }
          >
            <Code className="h-4 w-4" />
          </Toggle>
        )}

        {functionConfig.headingLevels &&
          functionConfig.headingLevels.length > 0 && (
            <Separator orientation="vertical" className="h-6" />
          )}

        {functionConfig.headingLevels?.includes(1) && (
          <Toggle
            size="sm"
            pressed={editor.isActive('heading', { level: 1 })}
            disabled={!editable}
            onPressedChange={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
          >
            <Heading1 className="h-4 w-4" />
          </Toggle>
        )}

        {functionConfig.headingLevels?.includes(2) && (
          <Toggle
            size="sm"
            pressed={editor.isActive('heading', { level: 2 })}
            disabled={!editable}
            onPressedChange={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
          >
            <Heading2 className="h-4 w-4" />
          </Toggle>
        )}

        {functionConfig.headingLevels?.includes(3) && (
          <Toggle
            size="sm"
            pressed={editor.isActive('heading', { level: 3 })}
            disabled={!editable}
            onPressedChange={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
          >
            <Heading3 className="h-4 w-4" />
          </Toggle>
        )}

        {(functionConfig.blockquote ||
          functionConfig.bulletList ||
          functionConfig.orderedList) && (
          <Separator orientation="vertical" className="h-6" />
        )}

        {functionConfig.bulletList && (
          <Toggle
            size="sm"
            pressed={editor.isActive('bulletList')}
            disabled={!editable}
            onPressedChange={() =>
              editor.chain().focus().toggleBulletList().run()
            }
          >
            <List className="h-4 w-4" />
          </Toggle>
        )}

        {functionConfig.orderedList && (
          <Toggle
            size="sm"
            pressed={editor.isActive('orderedList')}
            disabled={!editable}
            onPressedChange={() =>
              editor.chain().focus().toggleOrderedList().run()
            }
          >
            <ListOrdered className="h-4 w-4" />
          </Toggle>
        )}

        {functionConfig.blockquote && (
          <Toggle
            size="sm"
            pressed={editor.isActive('blockquote')}
            disabled={!editable}
            onPressedChange={() =>
              editor.chain().focus().toggleBlockquote().run()
            }
          >
            <Quote className="h-4 w-4" />
          </Toggle>
        )}

        {functionConfig.horizontalRule && (
          <Separator orientation="vertical" className="h-6" />
        )}

        {functionConfig.horizontalRule && (
          <Button
            variant="ghost"
            size="sm"
            disabled={!editable}
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
          >
            <Minus className="h-4 w-4" />
          </Button>
        )}

        {functionConfig.undoRedo && (
          <Separator orientation="vertical" className="h-6" />
        )}

        {functionConfig.undoRedo && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().chain().focus().undo().run() || !editable}
          >
            <Undo className="h-4 w-4" />
          </Button>
        )}

        {functionConfig.undoRedo && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().chain().focus().redo().run() || !editable}
          >
            <Redo className="h-4 w-4" />
          </Button>
        )}
      </div>

      {limit && (
        <div className="absolute bottom-1 right-2 text-sm text-muted-foreground">
          {editor.storage.characterCount.characters()}/{limit}
        </div>
      )}
      <EditorContent
        onFocus={onFocus}
        editor={editor}
        placeholder={placeholder}
        maxLength={10}
      />
    </div>
  );
}

export { MinimalTiptap, type MinimalTiptapProps };
